<template>
  <div class="row">
    <login-warning-overlay v-if="isApiAvailable === false" />
    <section class="banner section">
      <h1>
        Вход в панель управления {{ brand.name }}
      </h1>
      <div class="banner__illustration">
        <img
          :src="pc"
          class="banner__image -a"
        >
        <img
          :src="shield"
          class="banner__image -large -b"
        >
        <img
          :src="globe"
          class="banner__image -c"
        >
      </div>
    </section>
    <form
      class="login section"
      @submit.prevent="submit"
    >
      <div class="vpn__wires">
        <img
          class="vpn__wires-image"
          :src="wires"
        >
      </div>

      <fp-input
        v-model="v$.email.$model"
        type="email"
        placeholder="email"
      />
      <fp-input
        v-model="v$.password.$model"
        type="password"
        placeholder="пароль"
      />
      <fp-button
        primary
        :disabled="v$.$invalid"
        :pending
      >
        Войти
      </fp-button>
      <NuxtLink
        :to="{name: 'request-reset-pass'}"
      >
        Забыл пароль
      </NuxtLink>
      <div
        v-if="errorResponse"
        class="vpn__error"
      >
        Проверьте правильность введенных данных
      </div>
      <div
        v-if="serverErrorResponse"
        class="vpn__error"
      >
        Что-то пошло не так, попробуйте позже
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import { reactive, ref } from 'vue';
import wires from '~/assets/wires.svg?url';
import pc from '~/assets/pc.png';
import shield from '~/assets/shield.png';
import globe from '~/assets/globe.png';
import { useSessionStorage } from '@vueuse/core';
import type { ClientResponseError } from 'pocketbase';

const config = useRuntimeConfig();
const brand = config.public.brand;
const { isApiAvailable } = useSiteAvailability();

definePageMeta({
  // @ts-expect-error Nuxt types are wrong about this
  middleware: 'only-unauthed'
});

const {$pb} = useNuxtApp();

const router = useRouter();

const errorResponse = ref();
const serverErrorResponse = ref();
const pending = ref(false);
const form = reactive({
  email: '',
  password: ''
});

const rules = {
  email: { required, email },
  password: { required }
};

const v$ = useVuelidate(rules, form);

const submit = async () => {
  if (v$.value.$invalid) {
    return;
  }

  pending.value = true;

  try {
    errorResponse.value = false;
    serverErrorResponse.value = false;

    await $pb.collection('users').authWithPassword(
      form.email,
      form.password
    );

    const sessionStorage = useSessionStorage('after_login', '');

    if (sessionStorage.value !== '') {
      const afterLogin = JSON.parse(sessionStorage.value);
      sessionStorage.value = '';
      if (afterLogin.path) {
        await router.push(afterLogin.path);
      } else {
        await router.push('/');
      }
    } else {
      await router.push('/');
    }
  } catch (_error) {
    const error = _error as ClientResponseError;
    console.error(error);

    if (error.status === 400) {
      errorResponse.value = true;
    } else {
      serverErrorResponse.value = true;
    }
  } finally {
    pending.value = false;
  }
};
</script>

<style scoped lang="scss">
.vpn__wires {
  width: calc(100% + 128px);
  position: relative;
  flex-grow: 1;
  overflow: hidden;
  height: 200px;
  left: -64px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.vpn__wires-image {
  width: 100%;
  position: relative;
  display: block;
}

.vpn__error {
  color: var(--clr-red);
  letter-spacing: .1px;
}

.row {
  position: relative;
  flex-grow: 1;
  display: grid;
  gap: 16px;
  grid-template-rows: 1fr;
  grid-template-columns: auto min-content;
}

.safari .row {
  flex-grow: unset;
  height: -webkit-fill-available;
}

.banner {
  overflow: hidden;
  position: relative;
  background: var(--clr-focus);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.banner__illustration {
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  gap: 16px;

  @media screen and (max-width: 1080px) {
    justify-content: center;
  }
}

.banner__image {
  height: 150px;
  transition: transform .5s;

  &:hover {
    transform: rotate(0) !important;
  }

  &.-large {
    height: 210px;
  }

  &.-a {
    transform: rotate(2deg);
  }

  &.-b {
    transform: rotate(-1.5deg);
  }

  &.-c {
    transform: rotate(1.5deg);
  }
}

.login {
  position: relative;
  background: white;
  min-width: 300px;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

@media screen and (max-width: 720px) {
  .row {
    grid-template-columns: auto;
  }

  .banner {
    display: none;
  }

  .vpn__wires {
    width: calc(100% + 64px);
    left: -32px;
  }
}
</style>
